'use client';

import * as React from 'react';

import { useDevice } from '@/contexts/DeviceContext';
import { cn, DrawerPrimitive } from '@/libs';

type DesktopDirection = 'left' | 'right';
type MobileDirection = 'top' | 'bottom' | 'left' | 'right';

const Drawer = ({
  shouldScaleBackground = false,
  direction: desktopDirection = 'right',
  mobileDirection = 'bottom',
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root> & {
  direction?: DesktopDirection;
  mobileDirection?: MobileDirection;
}) => {
  const { isMobile } = useDevice();

  const direction = isMobile ? mobileDirection : desktopDirection;

  return (
    <DrawerPrimitive.Root
      shouldScaleBackground={shouldScaleBackground}
      autoFocus
      direction={direction}
      {...props}
    />
  );
};
Drawer.displayName = 'Drawer';

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 z-50 bg-black/80', className)}
    {...props}
  />
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
    drawerOverlayOptions?: React.ComponentProps<typeof DrawerOverlay>;
    direction?: DesktopDirection;
    mobileDirection?: MobileDirection;
  }
>(
  (
    {
      className,
      children,
      drawerOverlayOptions,
      direction: desktopDirection = 'right',
      mobileDirection = 'bottom',
      ...props
    },
    ref,
  ) => {
    const { isMobile } = useDevice();

    const getMobileClassName = () => {
      switch (mobileDirection) {
        case 'top':
          return 'fixed inset-x-0 top-0 mb-24 h-auto rounded-b-[10px]';
        case 'left':
          return 'fixed left-0 top-0 bottom-0 h-screen w-auto rounded-none';
        case 'right':
          return 'fixed right-0 top-0 bottom-0 h-screen w-auto rounded-none';
        case 'bottom':
        default:
          return 'fixed inset-x-0 bottom-0 mt-24 h-auto rounded-t-[10px]';
      }
    };

    const getDesktopClassName = () => {
      switch (desktopDirection) {
        case 'left':
          return 'fixed left-0 top-0 bottom-0 h-screen min-w-[368px] rounded-none';
        case 'right':
          return 'fixed right-0 top-0 bottom-0 h-screen min-w-[368px] rounded-none';
      }
    };

    return (
      <DrawerPortal>
        <DrawerOverlay {...drawerOverlayOptions} />
        {/* <DrawerOverlay className="fixed inset-0 bg-black/40" /> */}
        <DrawerPrimitive.Content
          ref={ref}
          className={cn(
            isMobile ? getMobileClassName() : getDesktopClassName(),
            'z-50 outline-none flex flex-col border-0 bg-background !px-0',
            className,
          )}
          aria-describedby={undefined}
          {...props}
        >
          {children}
        </DrawerPrimitive.Content>
      </DrawerPortal>
    );
  },
);
DrawerContent.displayName = 'DrawerContent';

const DrawerHeader = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'grid gap-1 pt-1 pb-5 px-4 text-left sm:text-left border-b border-foreground/5',
      className,
    )}
    {...props}
  >
    {children}
  </div>
);
DrawerHeader.displayName = 'DrawerHeader';

const DrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('mt-auto flex flex-col gap-2 p-4 pb-0', className)} {...props} />
);
DrawerFooter.displayName = 'DrawerFooter';

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn('text-md font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
};
